import { useState, useRef, useEffect, useCallback } from 'react'
import { api } from '../utils/api'
import { t } from '../i18n/index'
import type { Video } from '../types'

type WatermarkPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center'
type ActiveTab = 'crop' | 'merge' | 'rotate'

interface WatermarkConfig {
  enabled: boolean
  text: string
  fontSize: number
  position: WatermarkPosition
  fontColor: string
}

export default function EditPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('crop')
  const [videos, setVideos] = useState<Video[]>([])
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null)
  const [videoUrl, setVideoUrl] = useState<string>('')
  const [startTime, setStartTime] = useState(0)
  const [endTime, setEndTime] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [trimRange, setTrimRange] = useState<[number, number]>([0, 0])
  const [isDragging, setIsDragging] = useState<'start' | 'end' | 'range' | null>(null)
  const [dragStartX, setDragStartX] = useState(0)
  const [watermark, setWatermark] = useState<WatermarkConfig>({ enabled: false, text: '', fontSize: 24, position: 'bottom-right', fontColor: 'white' })
  const [previewWatermark, setPreviewWatermark] = useState(false)
  const [outputName, setOutputName] = useState('')
  const [exporting, setExporting] = useState(false)
  const [rotateDegrees, setRotateDegrees] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)

  useEffect(() => { api.listVideos().then((res: any) => setVideos(res.videos || [])).catch(console.error) }, [])

  const loadVideo = useCallback((v: Video) => {
    setSelectedVideo(v)
    if (videoUrl) URL.revokeObjectURL(videoUrl)
    setVideoUrl(`/api/videos/${v.id}/stream`)
    setStartTime(0); setEndTime(0); setTrimRange([0, 0])
    setDuration(0); setCurrentTime(0); setIsPlaying(false)
    setRotateDegrees(0); setOutputName('')
  }, [videoUrl])

  const handleTimelineMouseDown = (e: React.MouseEvent | React.TouchEvent, handle: 'start' | 'end' | 'range') => {
    e.preventDefault(); setIsDragging(handle)
    setDragStartX('touches' in e ? e.touches[0].clientX : e.clientX)
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !timelineRef.current) return
      const rect = timelineRef.current.getBoundingClientRect()
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const dt = ((clientX - dragStartX) / rect.width) * duration
      if (isDragging === 'start') { const n = Math.max(0, Math.min(trimRange[1] - 0.5, startTime + dt)); setStartTime(n); setTrimRange([n, trimRange[1]]) }
      else if (isDragging === 'end') { const n = Math.min(duration, Math.max(trimRange[0] + 0.5, endTime + dt)); setEndTime(n); setTrimRange([trimRange[0], n]) }
      else if (isDragging === 'range') { const r = trimRange[1] - trimRange[0]; const n = Math.max(0, Math.min(duration - r, startTime + dt)); setStartTime(n); setEndTime(n + r); setTrimRange([n, n + r]) }
      setDragStartX('touches' in e ? e.touches[0].clientX : e.clientX)
    }
    const handleMouseUp = () => setIsDragging(null)
    if (isDragging) { window.addEventListener('mousemove', handleMouseMove); window.addEventListener('mouseup', handleMouseUp)
      window.addEventListener('touchmove', handleMouseMove); window.addEventListener('touchend', handleMouseUp) }
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchmove', handleMouseMove); window.removeEventListener('touchend', handleMouseUp) }
  }, [isDragging, dragStartX, trimRange, startTime, endTime, duration])

  const handleTimeUpdate = () => { const t = videoRef.current?.currentTime ?? 0; setCurrentTime(t); if (t >= endTime && endTime > 0) videoRef.current!.currentTime = startTime }
  const handleLoadedMetadata = () => { const d = videoRef.current?.duration ?? 0; setDuration(d); setEndTime(d); setTrimRange([0, d]) }
  const togglePlay = () => { const v = videoRef.current; if (!v) return; if (isPlaying) { v.pause(); v.currentTime = startTime } else { v.currentTime = startTime; v.play() }; setIsPlaying(!isPlaying) }

  const handleExport = async () => {
    if (!selectedVideo) return
    setExporting(true)
    try {
      const params: any = { video_id: selectedVideo.id, start_time: startTime, end_time: endTime, output_filename: outputName || `clip_${selectedVideo.id}.mp4` }
      if (watermark.enabled && watermark.text) { Object.assign(params, { watermark_text: watermark.text, watermark_font_size: watermark.fontSize, watermark_position: watermark.position, watermark_font_color: watermark.fontColor }) }
      if (rotateDegrees !== 0) params.rotate = rotateDegrees
      const res = await api.cropVideo(params) as { success: boolean; task_id: string; status: string }
      if (res.success && res.task_id) setTimeout(() => api.downloadExport(res.task_id), 500)
      else alert('导出失败')
    } catch (e) { alert(String(e)) }
    finally { setExporting(false) }
  }

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
  const getWatermarkText = () => {
    if (!watermark.enabled || !watermark.text) return ''
    const now = new Date()
    return watermark.text.replace('{date}', now.toLocaleDateString('zh-CN')).replace('{time}', now.toLocaleTimeString('zh-CN')).replace('{datetime}', now.toLocaleString('zh-CN'))
  }

  return (
    <div className="flex gap-5 h-full">
      {/* Left: Preview + Timeline */}
      <div className="flex-1 flex flex-col gap-4 min-w-0">
        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-gray-200 rounded-sm p-1 w-fit">
          {([{ k: 'crop', l: t('edit.crop') }, { k: 'merge', l: t('edit.merge') }, { k: 'rotate', l: t('edit.rotate') }] as const).map(({ k, l }) => (
            <button key={k} onClick={() => setActiveTab(k)} className={`font-mono text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-sm transition-all duration-100 ${activeTab === k ? 'bg-gray-900 text-cyber-primary' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`}>{l}</button>
          ))}
        </div>

        {activeTab === 'crop' && (
          <>
            {/* Player */}
            <div className="cyber-card flex-1 flex flex-col min-h-0">
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400">{t('edit.preview')}</span>
                <span className="font-mono text-[10px] text-gray-400">{selectedVideo?.resolution || '—'} · {selectedVideo?.camera_angle?.toUpperCase() || '—'}</span>
              </div>
              <div className="flex-1 bg-gray-900 relative overflow-hidden">
                {selectedVideo ? (
                  <video ref={videoRef} src={videoUrl} className="w-full h-full object-contain" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onClick={togglePlay} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="font-mono text-3xl mb-3 opacity-30">▶</div>
                      <div className="font-mono text-xs text-gray-500 uppercase tracking-widest">{t('edit.select-video').replace('选择', '')}</div>
                    </div>
                  </div>
                )}
                {previewWatermark && selectedVideo && (
                  <div className="absolute pointer-events-none font-bold" style={{ fontSize: watermark.fontSize * 1.2, color: watermark.fontColor, textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
                    ...(watermark.position === 'top-left' && { top: '10%', left: '2%' }), ...(watermark.position === 'top-right' && { top: '10%', right: '2%' }),
                    ...(watermark.position === 'bottom-left' && { bottom: '10%', left: '2%' }), ...(watermark.position === 'bottom-right' && { bottom: '10%', right: '2%' }),
                    ...(watermark.position === 'center' && { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }),
                  }}>{getWatermarkText()}</div>
                )}
              </div>
              {/* Controls */}
              <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-3 flex-shrink-0">
                <button onClick={togglePlay} className="w-8 h-8 bg-gray-900 hover:bg-cyber-primary hover:text-gray-900 text-cyber-primary rounded-sm flex items-center justify-center transition-colors font-mono text-sm">{isPlaying ? '⏸' : '▶'}</button>
                <span className="font-mono text-[11px] text-gray-500 w-16 flex-shrink-0">{formatTime(currentTime)}</span>
                <input type="range" min={0} max={duration || 100} step={0.1} value={currentTime} onChange={(e) => { const t = parseFloat(e.target.value); setCurrentTime(t); if (videoRef.current) videoRef.current.currentTime = t }} className="flex-1 h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-gray-900" />
                <span className="font-mono text-[11px] text-gray-500 w-16 text-right flex-shrink-0">{formatTime(duration)}</span>
              </div>
            </div>

            {/* Trim bar */}
            <div className="cyber-card flex-shrink-0">
              <div className="px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400">{t('edit.trim')}</span>
                  <span className="font-mono text-[10px] text-cyber-primary font-bold">{formatTime(endTime - startTime)}</span>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="flex-1">
                    <div ref={timelineRef} className="h-6 bg-gray-100 rounded-sm relative cursor-pointer border border-gray-200">
                      <div className="absolute top-0 h-full bg-cyber-primary/20 border-x border-cyber-primary/40" style={{ left: `${(startTime / duration) * 100}%`, width: `${((endTime - startTime) / duration) * 100}%` }} />
                      <div className="absolute top-0 bottom-0 w-1 bg-cyber-primary cursor-ew-resize hover:scale-x-150 transition-transform" style={{ left: `${(startTime / duration) * 100}%`, transform: 'translateX(-50%)' }} onMouseDown={(e) => handleTimelineMouseDown(e, 'start')} onTouchStart={(e) => handleTimelineMouseDown(e, 'start')} />
                      <div className="absolute top-0 bottom-0 w-1 bg-cyber-primary cursor-ew-resize hover:scale-x-150 transition-transform" style={{ left: `${(endTime / duration) * 100}%`, transform: 'translateX(-50%)' }} onMouseDown={(e) => handleTimelineMouseDown(e, 'end')} onTouchStart={(e) => handleTimelineMouseDown(e, 'end')} />
                      <div className="absolute top-0 bottom-0 cursor-move" style={{ left: `${(startTime / duration) * 100}%`, width: `${((endTime - startTime) / duration) * 100}%` }} onMouseDown={(e) => handleTimelineMouseDown(e, 'range')} onTouchStart={(e) => handleTimelineMouseDown(e, 'range')} />
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <div>
                      <div className="font-mono text-[10px] text-gray-400 mb-1 uppercase tracking-wider">{t('edit.start')}</div>
                      <input type="number" step={0.1} value={startTime.toFixed(1)} onChange={(e) => { const t = parseFloat(e.target.value) || 0; setStartTime(Math.max(0, Math.min(t, endTime - 0.1))); setTrimRange([Math.max(0, Math.min(t, endTime - 0.1)), trimRange[1]]) }} className="cyber-input w-20 text-center font-mono text-xs" />
                    </div>
                    <div>
                      <div className="font-mono text-[10px] text-gray-400 mb-1 uppercase tracking-wider">{t('edit.end')}</div>
                      <input type="number" step={0.1} value={endTime.toFixed(1)} onChange={(e) => { const t = parseFloat(e.target.value) || 0; setEndTime(Math.min(duration, Math.max(t, startTime + 0.1))); setTrimRange([trimRange[0], Math.min(duration, Math.max(t, startTime + 0.1))]) }} className="cyber-input w-20 text-center font-mono text-xs" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'merge' && (
          <div className="cyber-card flex-1">
            <div className="px-5 py-3 border-b border-gray-100"><span className="font-mono text-xs font-bold uppercase tracking-widest text-gray-500">{t('edit.merge')}</span></div>
            <div className="p-5">
              {videos.length < 2 ? <div className="font-mono text-xs text-gray-400 uppercase tracking-wider">{t('timeline.no-video')}</div> : <>
                <div className="font-mono text-[10px] text-gray-400 uppercase tracking-widest mb-3">SELECT VIDEOS (ORDER MATTERS)</div>
                <div className="space-y-1.5 mb-4 max-h-48 overflow-y-auto">
                  {videos.map((v) => (<label key={v.id} className="flex items-center gap-3 p-2.5 bg-gray-50 border border-gray-200 rounded-sm cursor-pointer hover:border-cyber-primary/50 transition-colors">
                    <input type="checkbox" className="accent-cyber-primary w-3.5 h-3.5" />
                    <span className="font-mono text-xs font-bold text-gray-900 flex-1 truncate">{v.filename}</span>
                    <span className="font-mono text-[10px] text-gray-400">{v.duration ? formatTime(v.duration) : '--'}</span>
                  </label>))}
                </div>
                <button className="cyber-btn-primary">▶ START MERGE</button>
              </>}
            </div>
          </div>
        )}

        {activeTab === 'rotate' && (
          <div className="cyber-card flex-1">
            <div className="px-5 py-3 border-b border-gray-100"><span className="font-mono text-xs font-bold uppercase tracking-widest text-gray-500">{t('edit.rotate')}</span></div>
            <div className="p-5">
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[90, 180, 270].map((deg) => (<button key={deg} onClick={() => setRotateDegrees(deg)} className={`py-4 rounded-sm font-mono text-xs font-bold uppercase tracking-wider border transition-all duration-100 ${rotateDegrees === deg ? 'bg-gray-900 text-cyber-primary border-gray-900' : 'bg-white text-gray-500 border-gray-200 hover:border-cyber-primary hover:text-gray-900'}`}>{deg}°</button>))}
              </div>
              {selectedVideo && <video src={videoUrl} className="w-full max-h-48 rounded-sm bg-gray-900 mb-4 object-contain" style={{ transform: `rotate(${rotateDegrees}deg)` }} />}
              <button className="cyber-btn-primary">▶ APPLY ROTATION</button>
            </div>
          </div>
        )}
      </div>

      {/* Right panel */}
      <div className="w-60 flex-shrink-0 flex flex-col gap-3">
        {/* Source */}
        <div className="cyber-card">
          <div className="px-4 py-2.5 border-b border-gray-100"><span className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400">{t('edit.source')}</span></div>
          <div className="p-4"><select className="cyber-input font-mono text-xs" onChange={(e) => { const v = videos.find(v => v.id === e.target.value); if (v) loadVideo(v) }} value={selectedVideo?.id || ''}>
            <option value="">— SELECT —</option>
            {videos.map((v) => (<option key={v.id} value={v.id}>{v.filename}</option>))}
          </select></div>
        </div>

        {/* Watermark */}
        <div className="cyber-card">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
            <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400">{t('edit.watermark')}</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={watermark.enabled} onChange={(e) => setWatermark({ ...watermark, enabled: e.target.checked })} className="sr-only peer" />
              <div className="w-7 h-3.5 bg-gray-200 peer-checked:bg-cyber-primary rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:w-2.5 after:h-2.5 after:rounded-full after:transition-transform peer-checked:after:translate-x-3" />
            </label>
          </div>
          {watermark.enabled && (
            <div className="p-4 space-y-3">
              <div><div className="font-mono text-[10px] text-gray-400 mb-1.5 uppercase tracking-wider">{t('edit.watermark-text')}</div>
                <input type="text" value={watermark.text} onChange={(e) => setWatermark({ ...watermark, text: e.target.value })} placeholder="{datetime}" className="cyber-input font-mono text-xs" />
                <div className="font-mono text-[10px] text-gray-400 mt-1">{t('edit.placeholder')}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><div className="font-mono text-[10px] text-gray-400 mb-1.5 uppercase tracking-wider">{t('edit.watermark-size')}</div><input type="number" value={watermark.fontSize} onChange={(e) => setWatermark({ ...watermark, fontSize: parseInt(e.target.value) || 24 })} className="cyber-input font-mono text-xs" /></div>
                <div><div className="font-mono text-[10px] text-gray-400 mb-1.5 uppercase tracking-wider">{t('edit.watermark-color')}</div><select value={watermark.fontColor} onChange={(e) => setWatermark({ ...watermark, fontColor: e.target.value })} className="cyber-input font-mono text-xs"><option value="white">WHITE</option><option value="yellow">YELLOW</option><option value="red">RED</option><option value="green">GREEN</option></select></div>
              </div>
              <div><div className="font-mono text-[10px] text-gray-400 mb-1.5 uppercase tracking-wider">{t('edit.watermark-pos')}</div>
                <div className="grid grid-cols-3 gap-1">
                  {(['top-left','top-right','center','bottom-left','bottom-right'] as WatermarkPosition[]).map((pos) => (<button key={pos} onClick={() => setWatermark({ ...watermark, position: pos })} className={`px-1.5 py-1.5 rounded-sm text-[9px] font-mono font-bold uppercase tracking-wider border transition-all duration-100 ${watermark.position === pos ? 'bg-gray-900 text-cyber-primary border-gray-900' : 'bg-white text-gray-400 border-gray-200 hover:border-cyber-primary'}`}>{pos.split('-')[0]}</button>))}
                </div>
              </div>
              <button onClick={() => setPreviewWatermark(!previewWatermark)} className={`w-full font-mono text-[10px] font-bold uppercase tracking-wider py-2 rounded-sm border transition-colors duration-100 ${previewWatermark ? 'bg-cyber-primary/10 border-cyber-primary text-gray-900' : 'bg-white border-gray-200 text-gray-500 hover:border-cyber-primary'}`}>
                {previewWatermark ? '◈ HIDE PREVIEW' : '◇ PREVIEW'}
              </button>
              {previewWatermark && <div className="bg-gray-900 rounded-sm p-3 text-center"><div className="font-bold" style={{ fontSize: watermark.fontSize, color: watermark.fontColor, textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>{getWatermarkText() || 'WATERMARK'}</div></div>}
            </div>
          )}
        </div>

        {/* Output */}
        <div className="cyber-card">
          <div className="px-4 py-2.5 border-b border-gray-100"><span className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400">{t('edit.output')}</span></div>
          <div className="p-4 space-y-3">
            <div><div className="font-mono text-[10px] text-gray-400 mb-1.5 uppercase tracking-wider">{t('edit.filename')}</div><input type="text" value={outputName} onChange={(e) => setOutputName(e.target.value)} placeholder="auto-generated" className="cyber-input font-mono text-xs" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><div className="font-mono text-[10px] text-gray-400 mb-1.5 uppercase tracking-wider">{t('edit.format')}</div><select className="cyber-input font-mono text-xs"><option>MP4 (H.264)</option><option>MP4 (H.265)</option><option>MOV</option></select></div>
              <div><div className="font-mono text-[10px] text-gray-400 mb-1.5 uppercase tracking-wider">{t('edit.resolution')}</div><select className="cyber-input font-mono text-xs"><option>{t('generic.original')}</option><option>1080P</option><option>720P</option></select></div>
            </div>
            <div className="font-mono text-[10px] text-gray-400 bg-cyber-primary/5 border border-cyber-primary/20 rounded-sm p-2.5 uppercase tracking-wider">● CAR EXPORT: FILE DOWNLOADS TO BROWSER CACHE</div>
            {exporting && <div className="font-mono text-[10px] text-cyber-primary uppercase tracking-wider animate-pulse">⏳ {t('edit.exporting')}</div>}
            <button onClick={handleExport} disabled={exporting || !selectedVideo} className="cyber-btn-primary w-full justify-center disabled:opacity-40">{exporting ? `⏳ ${t('edit.exporting')}` : `▶ ${t('edit.export')}`}</button>
          </div>
        </div>

        {/* Tips */}
        <div className="font-mono text-[10px] text-gray-400 uppercase tracking-wider space-y-1 px-1">
          <div>• {t('edit.tip-drag')}</div>
          <div>• {t('edit.tip-range')}</div>
          <div>• {t('edit.tip-seek')}</div>
        </div>
      </div>
    </div>
  )
}
